import React, { useState } from 'react';
import { Reply, Check, X, Trash2, Pencil } from 'lucide-react';
import axios from 'axios';
import ChildCommentItem from './ChildCommentItem';
import UserAvatar from '../UserAvatar';
import ProfilePopover from '../ProfilePopover';

const CommentItem = ({
  comments,
  boardId,
  clientId,
  boardType,
  setComments,
  me,
  writer,
  nickName,
  parentComment,
  setCommentCount,
  setCommentUpdate,
}) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [childContent, setChildContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(parentComment?.content || '');
  const authorName = parentComment.client?.nickName || parentComment.client?.name;

  const isMyComment =
    clientId === Number(parentComment?.clientId) || me?.adminYn === 'Y';

  const childCommentList = comments
    .filter((c) => c.parent === parentComment?.id)
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const handleReply = () => {
    if (!childContent.trim()) return;
    const childComment = {
      boardId,
      clientId,
      writer: authorName || writer,
      content: childContent,
      parent: parentComment.id,
      boardType,
    };
    axios
      .post('/board/comment/write', childComment, { withCredentials: true })
      .then((response) => {
        setComments((prev) => [...prev, response.data]);
        setChildContent('');
        setShowReplyForm(false);
      })
      .catch(() => {
        alert('대댓글 등록 중 오류가 발생했습니다.');
      });
  };

  const handleEdit = () => {
    if (!editContent.trim()) return;
    axios
      .put(`/board/comment/modify/${parentComment.id}`, { content: editContent, boardType }, { withCredentials: true })
      .then(() => {
        setCommentUpdate((prev) => !prev);
        setIsEditing(false);
      });
  };

  const handleDelete = () => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    axios
      .delete(`/board/comment/${parentComment.id}?boardType=${boardType}`, { withCredentials: true })
      .then(() => {
        const updatedComments = comments.filter((c) => c.id !== parentComment.id);
        setComments(updatedComments);
        setCommentCount(updatedComments.filter((c) => !c.parent).length);
      });
  };

  return (
    <div className="rounded-xl border border-white/[0.08] overflow-hidden">
      {/* 댓글 본문 */}
      <div className="px-4 py-3 bg-[#182334]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <ProfilePopover user={{
              userType: 'client',
              userId: parentComment.client?.id,
              name: authorName,
              profileImage: parentComment.client?.profileImage,
              adminYn: parentComment.client?.adminYn,
            }}>
              <UserAvatar
                name={authorName}
                profileImage={parentComment.client?.profileImage}
                userType="client"
                adminYn={parentComment.client?.adminYn}
                size={24}
              />
              <span className="text-sm font-semibold text-slate-200 hover:text-sky-400 transition-colors">
                {authorName}
              </span>
            </ProfilePopover>
            <span className="text-[11px] text-slate-500">
              {new Date(parentComment?.createdAt).toLocaleDateString('ko-KR')}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              className="p-1 text-slate-500 hover:text-sky-400 transition-colors"
              onClick={() => setShowReplyForm(!showReplyForm)}
              title="대댓글">
              <Reply size={14} />
            </button>
            {isMyComment && !isEditing && (
              <>
                <button
                  className="p-1 text-slate-500 hover:text-sky-400 transition-colors"
                  onClick={() => setIsEditing(true)}
                  title="수정">
                  <Pencil size={13} />
                </button>
                <button
                  className="p-1 text-slate-500 hover:text-rose-400 transition-colors"
                  onClick={handleDelete}
                  title="삭제">
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* 본문 / 수정 모드 */}
        <div className="mt-2 ml-8">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <input
                className="flex-1 px-3 py-1.5 rounded-lg border border-white/[0.08] text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent bg-[#1a2b42] text-slate-200"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEdit()}
                autoFocus
              />
              <button
                className="p-1.5 text-sky-400 hover:text-sky-300 transition-colors"
                onClick={handleEdit}
                title="완료">
                <Check size={15} />
              </button>
              <button
                className="p-1.5 text-slate-500 hover:text-slate-300 transition-colors"
                onClick={() => { setIsEditing(false); setEditContent(parentComment?.content || ''); }}
                title="취소">
                <X size={15} />
              </button>
            </div>
          ) : (
            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
              {parentComment?.content}
            </p>
          )}
        </div>
      </div>

      {/* 대댓글 작성 폼 */}
      {showReplyForm && (
        <div className="ml-8 px-4 py-3 border-t border-white/[0.06] bg-white/[0.02] flex items-center gap-3">
          <span className="text-xs font-medium text-slate-500 shrink-0">
            {nickName || writer}
          </span>
          <input
            className="flex-1 px-3 py-1.5 rounded-lg border border-white/[0.08] text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent bg-[#1a2b42] text-slate-200 placeholder-slate-500"
            value={childContent}
            onChange={(e) => setChildContent(e.target.value)}
            placeholder="대댓글을 입력하세요"
            onKeyDown={(e) => e.key === 'Enter' && handleReply()}
            autoFocus
          />
          <button
            className="shrink-0 px-3 py-1.5 bg-sky-500 rounded-lg hover:bg-sky-600 text-white text-xs font-semibold transition-colors disabled:bg-slate-700 disabled:text-slate-500"
            onClick={handleReply}
            disabled={!childContent.trim()}>
            등록
          </button>
        </div>
      )}

      {/* 대댓글 목록 */}
      {childCommentList.length > 0 &&
        childCommentList.map((childComment) => (
          <div
            key={childComment.id}
            className="ml-8 px-4 py-2.5 border-t border-white/[0.06] bg-white/[0.02]">
            <ChildCommentItem
              comments={comments}
              setComments={setComments}
              clientId={clientId}
              me={me}
              boardType={boardType}
              childComment={childComment}
              setCommentUpdate={setCommentUpdate}
            />
          </div>
        ))}
    </div>
  );
};

export default CommentItem;
